import { blinkConstants } from '@thoughtspot/blink-constants';
import { useEmbedContext } from '@thoughtspot/blink-context';
import { create } from '@thoughtspot/logger';
import cx from 'classnames';
import _ from 'lodash';
import React from 'react';
import ReactModal from 'react-modal';
import { Button } from '../button';
import { MultiSelectButton, } from '../button/multi-select-button';
import { IconID } from '../icon';
import { LoadingIndicator } from '../loading-indicator';
import { Typography } from '../typography';
import styles from './modal.module.scss';
const logger = create('modal');
ReactModal.setAppElement('body');
ReactModal.defaultStyles.overlay.backgroundColor = 'rgba(28,35,48,.5)';
export var ModalTestId;
(function (ModalTestId) {
    ModalTestId["CONFIRM_BUTTON"] = "modal-confirm-button";
    ModalTestId["CROSS_BUTTON"] = "modal-cross-button";
    ModalTestId["DENY_BUTTON"] = "modal-deny-button";
    ModalTestId["DISMISS_BUTTON"] = "modal-dismiss-button";
    ModalTestId["CONTAINER"] = "modal-container";
})(ModalTestId || (ModalTestId = {}));
export var ModalSize;
(function (ModalSize) {
    ModalSize["SMALL"] = "small";
    ModalSize["MEDIUM"] = "medium";
    ModalSize["MEDIUM3"] = "medium3";
    ModalSize["LARGE"] = "large";
    ModalSize["FULLSCREEN"] = "fullscreen";
})(ModalSize || (ModalSize = {}));
export var ModalType;
(function (ModalType) {
    ModalType["Alert"] = "alert";
    ModalType["NoAlert"] = "no-alert";
    ModalType["Wizard"] = "wizard";
})(ModalType || (ModalType = {}));
/**
 * Hook for listening modal open and close events
 * SCAL-102816
 * @param isOpen
 */
const useEmbedModalEvents = (isOpen) => {
    const { dispatchEmbedEvent } = useEmbedContext();
    React.useEffect(() => {
        if (isOpen) {
            dispatchEmbedEvent(blinkConstants.embedEvents.DialogOpen, {});
        }
        return () => {
            // Dispatch close event only when modal is opened before component unmounts
            if (isOpen) {
                dispatchEmbedEvent(blinkConstants.embedEvents.DialogClose, {});
            }
        };
    }, [dispatchEmbedEvent, isOpen]);
};
export const Modal = ({ variant = ModalType.NoAlert, isOpen, size = ModalSize.MEDIUM, title = '', titleTooltip = '', titleTooltipConfig, includesHeader = true, includesFooter = true, children, autoScrollContent = true, isContentLoading, contentLoadingText, confirmButton, multiSelectConfirmButton, dismissButton, denyButton, hasCrossButton = false, backButton, doNotDismissOnOutsideClickOrEsc = false, onDismiss = _.noop, // TODO(Rifdhan) can we not use defaults?
onAfterClose = _.noop, alwaysMaxHeight = false, noPadding = false, className = '', contentClassName = '', isContentHeightDynamic = false, shouldScaleForSmallerScreens = false, modalTestId, customHeaderClassName = '', enablePrimaryButton = false, customFooterClassName = '', customLoaderClassName = '', customLoadingTextClassName = '', customPortalClassName = '', }) => {
    const isModalWizard = variant === ModalType.Wizard;
    const isModalAlert = variant === ModalType.Alert;
    const [embedClass, setEmbedClass] = React.useState({});
    const { dispatchEmbedEvent, embedParams } = useEmbedContext();
    const isFullHeightPinboard = embedParams === null || embedParams === void 0 ? void 0 : embedParams.isFullHeightPinboard;
    const [openModal, setOpenModal] = React.useState(!isFullHeightPinboard && isOpen);
    useEmbedModalEvents(isOpen);
    React.useEffect(() => {
        const asyncFunc = async () => {
            try {
                const style = {};
                const embedModalCenter = await dispatchEmbedEvent(blinkConstants.embedEvents.EmbedIframeCenter, {});
                const top = Math.max(0, embedModalCenter.data.iframeScrolled);
                const bottom = embedModalCenter.data.iframeHeight -
                    embedModalCenter.data.iframeVisibleViewPort -
                    top;
                style.paddingTop = top;
                style.paddingBottom = bottom;
                setEmbedClass({ overlay: style });
            }
            catch (error) {
                logger.error(error);
            }
            finally {
                setOpenModal(isOpen);
            }
        };
        if (isFullHeightPinboard) {
            asyncFunc();
        }
        else {
            setOpenModal(isOpen);
        }
    }, [dispatchEmbedEvent, isFullHeightPinboard, isOpen]);
    const renderHeader = () => {
        var _a, _b, _c;
        const typographyTitleVariant = isModalAlert
            ? 'section-label'
            : 'modal-title';
        return (React.createElement("div", { className: cx(styles.header, customHeaderClassName) },
            backButton && (React.createElement(Button, { className: styles.backBtn, type: "secondary", size: "m", icon: IconID.ARROW_LEFT, onClick: backButton.onBack, "data-tooltip-content": "Back" })),
            React.createElement(Typography, { variant: typographyTitleVariant, ellipsis: { rows: 1 }, tooltipConfig: {
                    disableTooltip: (_a = titleTooltipConfig === null || titleTooltipConfig === void 0 ? void 0 : titleTooltipConfig.disableTooltip) !== null && _a !== void 0 ? _a : false,
                    showTooltipOnlyOnEllipsis: (_b = titleTooltipConfig === null || titleTooltipConfig === void 0 ? void 0 : titleTooltipConfig.showTooltipOnlyOnEllipsis) !== null && _b !== void 0 ? _b : true,
                    dataTooltipContent: (_c = titleTooltipConfig === null || titleTooltipConfig === void 0 ? void 0 : titleTooltipConfig.dataTooltipContent) !== null && _c !== void 0 ? _c : title,
                }, noMargin: true }, title),
            titleTooltip && (React.createElement(Button, { className: styles.infoIcon, icon: IconID.INFORMATION_CIRCLE, "data-tooltip-content": titleTooltip, type: "secondary", size: "s" }))));
    };
    const footer = (React.createElement("div", { className: cx(styles.footer, styles[variant], customFooterClassName) },
        React.createElement("div", { className: styles.denyBtnWrapper }, !isModalAlert && denyButton && (React.createElement(Button, { type: "tertiary", isDisabled: isContentLoading || denyButton.isDisabled, onClick: denyButton.onDeny, text: denyButton.text, "data-tooltip-content": denyButton.tooltip, isSaving: denyButton.isLoading, "data-testid": ModalTestId.DENY_BUTTON, className: denyButton.className }))),
        dismissButton && (React.createElement(Button, { className: cx(styles.actionBtn, dismissButton.className), type: "secondary", isDisabled: dismissButton.isDisabled, onClick: dismissButton.onClick || onDismiss, text: dismissButton.text, "data-tooltip-content": dismissButton.tooltip, isSaving: dismissButton.isLoading, "data-testid": ModalTestId.DISMISS_BUTTON })),
        confirmButton && (React.createElement(Button, { className: cx(styles.actionBtn, confirmButton.className), type: "primary", isDisabled: enablePrimaryButton
                ? false
                : isContentLoading || confirmButton.isDisabled, onClick: confirmButton.onConfirm, text: confirmButton.text, "data-tooltip-content": confirmButton.tooltip, isSaving: confirmButton.isLoading, "data-testid": ModalTestId.CONFIRM_BUTTON })),
        multiSelectConfirmButton && (React.createElement(MultiSelectButton, { className: styles.actionBtn, ...multiSelectConfirmButton }))));
    const renderChildren = () => {
        const contentClasses = cx(styles.content, styles[variant], contentClassName, {
            [styles.autoScrollContent]: autoScrollContent,
            [styles.noPadding]: noPadding,
            [styles.contentWithHeaderAbove]: isModalAlert && title,
            [customLoaderClassName]: isContentLoading,
        });
        return (React.createElement(LoadingIndicator.Global, { hideLoader: !isContentLoading, className: styles.loadingWrapper, loadingTextClassName: customLoadingTextClassName, loadingText: contentLoadingText !== null && contentLoadingText !== void 0 ? contentLoadingText : '' },
            React.createElement("div", { className: contentClasses }, children)));
    };
    return (React.createElement(ReactModal, { isOpen: openModal, shouldCloseOnOverlayClick: !doNotDismissOnOutsideClickOrEsc, shouldCloseOnEsc: !doNotDismissOnOutsideClickOrEsc, ariaHideApp: false, 
        // This must match the transition time value for ReactModal__Overlay in the CSS
        closeTimeoutMS: 200, className: cx(className, styles.modal, styles[variant], isModalAlert ? styles[ModalSize.SMALL] : styles[size], {
            [styles.alwaysMaxHeight]: alwaysMaxHeight,
            [styles.isModalWizard]: isModalWizard,
            [styles.isContentHeightDynamic]: isContentHeightDynamic,
            [styles.shouldScaleForSmallerScreens]: shouldScaleForSmallerScreens,
        }), style: isFullHeightPinboard ? embedClass : {}, onRequestClose: onDismiss, onAfterClose: onAfterClose, testId: modalTestId !== null && modalTestId !== void 0 ? modalTestId : ModalTestId.CONTAINER, portalClassName: cx('ReactModalPortal', customPortalClassName) },
        includesHeader && renderHeader(),
        !isModalAlert && hasCrossButton && (React.createElement(Button, { className: styles.crossBtn, icon: IconID.CROSS, onClick: onDismiss, ariaLabel: "Close Modal", type: "tertiary", "data-testid": ModalTestId.CROSS_BUTTON })),
        children && renderChildren(),
        includesFooter && footer));
};
//# sourceMappingURL=modal.js.map